/* =====================================================================
   Owl + Fox — Collection v3.0 Add-ons
   Requires: collection-v2.js (loaded first), Alpine.js
   Features:
   · Sticky toolbar shadow via IntersectionObserver
   · Grid density toggle with localStorage persistence
   · Filter list "Show N more / less" collapsing
   · Vanilla JS filter / sort / drawer (robust, no Alpine dependency)
   ===================================================================== */

(function () {
  'use strict';

  /* ── Constants ── */
  var DENSITY_STORAGE_KEY = 'of-coll-density';
  var DENSITY_DEFAULT     = '4';
  var FILTER_SHOW_LIMIT   = 7;

  /* ================================================================
     1. STICKY TOOLBAR SHADOW
     ================================================================ */
  function initStickyToolbar() {
    var sentinel = document.getElementById('coll-toolbar-sentinel');
    var toolbar  = document.getElementById('coll-toolbar');
    if (!sentinel || !toolbar) return;

    var obs = new IntersectionObserver(
      function (entries) {
        toolbar.classList.toggle('is-floating', !entries[0].isIntersecting);
      },
      { rootMargin: '-1px 0px 0px 0px', threshold: 0 }
    );

    obs.observe(sentinel);
  }

  /* ================================================================
     2. GRID DENSITY TOGGLE
     ================================================================ */
  var _currentDensity = DENSITY_DEFAULT;

  function initDensityToggle() {
    var saved = getStorage(DENSITY_STORAGE_KEY) || DENSITY_DEFAULT;
    _currentDensity = saved;
    applyDensity(saved);

    var btns = document.querySelectorAll('.coll-v2__density-btn');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var d = btn.getAttribute('data-density');
        _currentDensity = d;
        applyDensity(d);
        setStorage(DENSITY_STORAGE_KEY, d);
      });
    });
  }

  function applyDensity(density) {
    var grid = document.getElementById('coll-grid');
    var btns = document.querySelectorAll('.coll-v2__density-btn');
    if (grid) {
      grid.classList.remove('coll-v2__grid--density-3', 'coll-v2__grid--density-4');
      grid.classList.add('coll-v2__grid--density-' + density);
    }
    btns.forEach(function (btn) {
      btn.classList.toggle('is-active', btn.getAttribute('data-density') === density);
    });
  }

  window.ofV3DensityRefresh = function () {
    applyDensity(_currentDensity);
  };

  /* ================================================================
     3. FILTER LIST "SHOW MORE / LESS"
     ================================================================ */
  function initShowMore() {
    document.querySelectorAll('.flt-v2-list').forEach(function (list) {
      var items = list.querySelectorAll('li');
      if (items.length <= FILTER_SHOW_LIMIT) return;

      var hiddenCount = 0;
      items.forEach(function (li, idx) {
        if (idx >= FILTER_SHOW_LIMIT) {
          li.classList.add('flt-v2-item--collapsed');
          hiddenCount++;
        }
      });

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'flt-v2-more-btn';
      btn.setAttribute('aria-expanded', 'false');
      btn.textContent = 'Show ' + hiddenCount + ' more';

      btn.addEventListener('click', function () {
        var expanded = list.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', expanded.toString());
        btn.textContent = expanded ? 'Show less' : 'Show ' + hiddenCount + ' more';
      });

      list.parentNode.insertBefore(btn, list.nextSibling);
    });
  }

  /* ================================================================
     4. PRICE RANGE SLIDER
     ================================================================ */
  function ofInitPriceSlider(ctx) {
    var root = ctx || document;
    root.querySelectorAll('.flt-v2-psl:not([data-psl-ready])').forEach(function (sl) {
      sl.setAttribute('data-psl-ready', '1');

      var lo       = sl.querySelector('.flt-v2-psl__thumb--lo');
      var hi       = sl.querySelector('.flt-v2-psl__thumb--hi');
      var fill     = sl.querySelector('.flt-v2-psl__fill');
      var loVal    = sl.querySelector('.flt-v2-psl__lo-val');
      var hiVal    = sl.querySelector('.flt-v2-psl__hi-val');
      var loHidden = sl.querySelector('.flt-v2-psl__input-lo');
      var hiHidden = sl.querySelector('.flt-v2-psl__input-hi');
      if (!lo || !hi || !fill) return;

      var min   = parseFloat(sl.dataset.min);
      var max   = parseFloat(sl.dataset.max);
      var range = max - min || 1;
      var step  = Math.max(1, Math.round(range / 200));
      lo.step = hi.step = step;

      function fmt(n) { return Math.round(n).toLocaleString('en-ZA'); }

      function update() {
        var lv = parseFloat(lo.value);
        var hv = parseFloat(hi.value);
        if (lv > hv - step) {
          if (document.activeElement === lo) { lv = hv - step; lo.value = lv; }
          else                               { hv = lv + step; hi.value = hv; }
        }
        var loP = (lv - min) / range * 100;
        var hiP = (hv - min) / range * 100;
        fill.style.left  = loP + '%';
        fill.style.width = (hiP - loP) + '%';
        if (loVal) loVal.textContent = fmt(lv);
        if (hiVal) hiVal.textContent = fmt(hv);
        if (loHidden) loHidden.value = lv <= min ? '' : Math.round(lv);
        if (hiHidden) hiHidden.value = hv >= max ? '' : Math.round(hv);
      }

      lo.addEventListener('input', function () { lo.style.zIndex = '5'; hi.style.zIndex = '4'; update(); });
      hi.addEventListener('input', function () { hi.style.zIndex = '5'; lo.style.zIndex = '4'; update(); });
      update();
    });
  }

  window.ofInitPriceSlider = ofInitPriceSlider;

  /* ================================================================
     5. VANILLA JS FILTER SUBMISSION
     Works regardless of Alpine initialisation status.
     Tries Alpine AJAX first; falls back to full navigation.
     ================================================================ */
  function _buildFilterUrl() {
    var url = new URL(window.location.pathname, window.location.origin);

    /* Preserve current sort */
    var currentSort = new URLSearchParams(window.location.search).get('sort_by');
    if (currentSort) url.searchParams.set('sort_by', currentSort);
    url.searchParams.delete('page');

    var seen = {};

    document.querySelectorAll('.coll-v2__flt-form').forEach(function (form) {
      /* Only checked checkboxes — FormData would also include hidden inputs that
         mirror active filter values inside the price block, preventing deselection */
      form.querySelectorAll('input[type="checkbox"]:checked').forEach(function (input) {
        if (!input.name || !input.value) return;
        var dk = input.name + '=' + input.value;
        if (seen[dk]) return;
        seen[dk] = true;
        url.searchParams.append(input.name, input.value);
      });

      /* Price slider hidden inputs (min/max values set by the range slider JS) */
      form.querySelectorAll('.flt-v2-psl__input-lo, .flt-v2-psl__input-hi').forEach(function (input) {
        if (input.name && input.value !== '') {
          url.searchParams.set(input.name, input.value);
        }
      });
    });

    return url.toString();
  }

  function _navigateOrAjax(url) {
    /* Prefer Alpine AJAX for a no-reload experience */
    var el = document.getElementById('coll-v2-page');
    var alpine = el && el._x_dataStack && el._x_dataStack[0];
    if (alpine && typeof alpine._fetchResults === 'function') {
      alpine._fetchResults(url, true);
      return;
    }
    /* Fallback: full page navigation (always works) */
    window.location.href = url;
  }

  var _filterTimer = null;

  function initFilterForms() {
    /* Listen for checkbox / radio changes on all filter forms */
    document.addEventListener('change', function (e) {
      var form = e.target.closest('.coll-v2__flt-form');
      if (!form) return;
      /* Ignore number inputs (price range — submitted via button) */
      if (e.target.type === 'number') return;
      /* Ignore the sort select (handled separately) */
      if (e.target.id === 'coll-sort') return;

      /* Sync this checkbox's state to the mirror checkbox in every other filter form
         (sidebar rail + mobile drawer both render the same checkboxes; if only one is
         updated the other would ghost-submit the old active state on next read) */
      if (e.target.type === 'checkbox') {
        var _name    = e.target.name;
        var _val     = e.target.value;
        var _checked = e.target.checked;
        document.querySelectorAll('.coll-v2__flt-form').forEach(function (f) {
          if (f === form) return;
          var mirror = f.querySelector(
            'input[type="checkbox"][name="' + CSS.escape(_name) + '"][value="' + CSS.escape(_val) + '"]'
          );
          if (mirror) mirror.checked = _checked;
        });
      }

      clearTimeout(_filterTimer);
      _filterTimer = setTimeout(function () {
        _navigateOrAjax(_buildFilterUrl());
      }, 80);
    });

    /* Price "Apply" button submits the form */
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('.coll-v2__flt-form');
      if (!form) return;
      e.preventDefault();
      _navigateOrAjax(_buildFilterUrl());
    });
  }

  /* ================================================================
     6. VANILLA JS SORT
     ================================================================ */
  function initSortSelect() {
    document.addEventListener('change', function (e) {
      if (e.target.id !== 'coll-sort') return;
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      url.searchParams.delete('page');
      _navigateOrAjax(url.toString());
    });
  }

  /* ================================================================
     7. VANILLA JS MOBILE DRAWER TOGGLE
     Adds / removes .is-open on drawer + scrim without requiring
     the Alpine collV2 component to be initialised.
     ================================================================ */
  function initMobileDrawer() {
    var drawer = document.getElementById('coll-drawer');
    var scrim  = document.querySelector('.coll-v2__scrim');
    if (!drawer) return;

    function openDrawer() {
      drawer.classList.add('is-open');
      if (scrim) scrim.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
    function closeDrawer() {
      drawer.classList.remove('is-open');
      if (scrim) scrim.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    /* Open buttons (.coll-v2__flt-btn can appear in toolbar and elsewhere) */
    document.addEventListener('click', function (e) {
      if (e.target.closest('.coll-v2__flt-btn')) { openDrawer(); return; }
      if (e.target.closest('.coll-v2__drawer-close')) { closeDrawer(); return; }
    });

    /* Scrim click closes */
    if (scrim) scrim.addEventListener('click', closeDrawer);

    /* Escape key closes */
    document.addEventListener('keyup', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });
  }

  /* ================================================================
     8. HOOK INTO AJAX FILTER UPDATES (Alpine, when available)
     ================================================================ */
  function patchAlpineCollV2() {
    var attempts = 0;
    var poll = setInterval(function () {
      attempts++;
      var el = document.getElementById('coll-v2-page');
      if (!el) { if (attempts > 20) clearInterval(poll); return; }

      var stack = el._x_dataStack;
      if (!stack || !stack[0] || typeof stack[0]._fetchResults !== 'function') {
        if (attempts > 20) clearInterval(poll);
        return;
      }

      clearInterval(poll);

      var orig = stack[0]._fetchResults.bind(stack[0]);
      stack[0]._fetchResults = function (url, pushState) {
        return orig(url, pushState).then(function () {
          window.ofV3DensityRefresh && window.ofV3DensityRefresh();
          ofInitPriceSlider();
        });
      };
    }, 200);
  }

  /* ================================================================
     INIT
     ================================================================ */
  function init() {
    initStickyToolbar();
    initDensityToggle();
    initShowMore();
    ofInitPriceSlider();
    initFilterForms();
    initSortSelect();
    initMobileDrawer();
    patchAlpineCollV2();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── LocalStorage helpers ── */
  function getStorage(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }
  function setStorage(key, val) {
    try { localStorage.setItem(key, val); } catch (e) {}
  }

})();
